import styles from "./SearchContainer.module.scss";
import { VLInput } from "../../../../../uikit/FormFields/VLInput/VLInput";
import { ConfigProvider, Form } from "antd";
import { useState, useMemo, useEffect } from "react";
import classNames from "classnames";
import {
  CloseIcon,
  FiltersIcon,
  SearchIcon,
  VisualSimilarityIcon,
} from "./Icons";
import { SuggestionsMenu } from "./SuggestionsMenu/SiggestionsMenu";
import { ImageSearch } from "./ImageSearch/ImageSearch";
import { FilterItem, FiltersMenu } from "./FiltersMenu/FiltersMenu";
import RelevanceButton from "./RelevanceButton";
import { DisabledSearchView } from "./DisabledSearchView/DisabledSearchView";
import { useSearchContainer } from "./useSearchContainer";

export type SearchContainerProps = {
  datasetId: string;
  logSearchAddition: () => void;
  logSearchRemoval: () => void;
  className?: string;
};

export const SearchContainer = ({
  datasetId,
  className,
  logSearchAddition,
  logSearchRemoval,
}: SearchContainerProps) => {
  const [filterSearchQuery, setFilterSearchQuery] = useState("");
  const {
    mode,
    isOpen,
    handleSearchByText,
    searchTextParam,
    query,
    form,
    inputPlaceholder,
    inputRef,
    showRelevanceButton,
    handleClose,
    ref,
    onSavedViewClick,
    showMode,
    handleAskChatClick,
    recentSearches,
    savedViews,
    handleInputFocus,
    handleInputChange,
    isSearchEnabled,
    isChatEnabled,
    handleLeftIconClick,
    pastedImageFile,
    customMetadataData,
  } = useSearchContainer({
    datasetId,
    logSearchAddition,
    logSearchRemoval,
  });
  // Reset search query when filter menu closes
  useEffect(() => {
    if (!isOpen && filterSearchQuery) {
      setFilterSearchQuery("");
    }
  }, [isOpen, filterSearchQuery]);

  const leftIcon = useMemo(() => {
    switch (mode) {
      case "search":
        return <SearchIcon />;
      case "image":
        return <VisualSimilarityIcon />;
      case "filters":
      case "filter-item":
      case "filter-search":
        return (
          <div className={styles.leftContainer}>
            <VLInput
              rootClassName={styles.searchFilterInput}
              placeholder={"Search Filters..."}
              className={styles.input}
              prefix={<SearchIcon />}
              onChange={(e) => setFilterSearchQuery(e.target.value)}
            />
          </div>
        );
    }
  }, [mode]);
  const isSearchMode = mode === "search";
  const isFilterMode = mode === "filter-item" || mode === "filters";

  return (
    <div className={classNames(styles.SearchContainer, className)} ref={ref}>
      <div
        className={classNames(styles.search, {
          [styles.menuOpen]: isOpen,
        })}
      >
        <div
          className={classNames(styles.iconContainer, {
            [styles.active]: isSearchMode && searchTextParam,
            [styles.hoverable]: isSearchMode || mode === "filter-item",
            [styles.filterItemActive]: isFilterMode,
          })}
          onMouseDown={handleLeftIconClick}
        >
          {leftIcon}
        </div>
        {isSearchEnabled ? (
          <ConfigProvider
            theme={{
              components: {
                Input: {
                  fontSize: 16,
                  colorBorder: "transparent",
                  activeBorderColor: "transparent",
                  hoverBorderColor: "transparent",
                },
              },
            }}
          >
            <Form
              id="search"
              form={form}
              className={styles.form}
              hidden={mode !== "search"}
              onFinish={({ query }) => handleSearchByText(query)}
            >
              <Form.Item
                name="query"
                style={{ marginBottom: 0, marginRight: 16 }}
              >
                <VLInput
                  ref={inputRef}
                  onFocus={handleInputFocus}
                  onClick={handleInputFocus}
                  placeholder={inputPlaceholder}
                  className={styles.input}
                  onChange={handleInputChange}
                />
              </Form.Item>
            </Form>
          </ConfigProvider>
        ) : (
          !isFilterMode && <DisabledSearchView datasetId={datasetId} />
        )}
        <div style={{ flexGrow: 1 }} />
        <div className={styles.icons}>
          {(query || !isSearchMode) && (
            <CloseIcon className={styles.clearInput} onClick={handleClose} />
          )}
          {showRelevanceButton && (
            <RelevanceButton className={styles.relevance} />
          )}
          {mode === "search" && (
            <>
              <div
                className={classNames(styles.iconContainer, styles.hoverable)}
                onClick={() => showMode("image")}
              >
                <VisualSimilarityIcon />
              </div>
              <div
                className={classNames(styles.iconContainer, styles.hoverable)}
                onClick={() => showMode("filters")}
              >
                <FiltersIcon />
              </div>
            </>
          )}
        </div>
      </div>
      {isOpen && (
        <>
          {isSearchMode && (
            <SuggestionsMenu
              query={query}
              className={styles.menu}
              datasetId={datasetId}
              onSavedViewClick={onSavedViewClick}
              onRecentSearchClick={handleSearchByText}
              onAskChatClick={handleAskChatClick}
              recentSearches={recentSearches}
              savedViews={savedViews}
              isChatEnabled={isChatEnabled}
            />
          )}
          {mode === "image" && (
            <ImageSearch
              className={styles.menu}
              onUpload={handleClose}
              initialFile={pastedImageFile}
            />
          )}
          {mode === "filters" && (
            <FiltersMenu
              key={`filters-${filterSearchQuery}`}
              datasetId={datasetId}
              className={styles.menu}
              onItemClick={() => showMode("filter-item")}
              searchQuery={filterSearchQuery}
              customMetadataData={customMetadataData} // Add this prop
            />
          )}
          {mode === "filter-item" && <FilterItem className={styles.menu} />}
        </>
      )}
    </div>
  );
};
